<template>
  <div class="mt-4 border-bottom">
    <div class="flex items-center justify-between mb-4">
      <div class="flex flex-col gap-1">
        <p class="text-left text-grey-light-900 text-md font-medium">Cancellation policy</p>
        <p class="text-left text-sm text-brand-700">
          If you choose to cancel this payment plan the following fees will apply.
        </p>
      </div>
      <Button
        @click="showCancellationPolicy = !showCancellationPolicy"
        class="ml-auto"
        variant="ghost"
        size="sm"
        type="button"
      >
        <ChevronDown class="transition-all" :class="{ 'rotate-180': showCancellationPolicy }" />
      </Button>
    </div>
    <Transition
      enter-active-class="transition-all duration-300 ease-out"
      enter-from-class="opacity-0 max-h-0 overflow-hidden"
      enter-to-class="opacity-100 max-h-96 overflow-visible"
      leave-active-class="transition-all duration-300 ease-in"
      leave-from-class="opacity-100 max-h-96 overflow-visible"
      leave-to-class="opacity-0 max-h-0 overflow-hidden"
    >
      <template v-if="showCancellationPolicy">
        <div>
          <div v-for="(term, index) in cancellationTerms" :key="index">
            <div v-show="term.start" class="text-left">
              <div class="flex flex-row my-2 rounded-lg overflow-hidden">
                <div class="flex-1 text-center bg-brand-500 p-2 w-1/2 border-r border-grey-light-200">
                  <p class="text-enter text-base-white text-sm">After {{ term.start }}</p>
                </div>
              </div>
            </div>
            <div class="text-left">
              <div class="flex flex-row my-2 rounded-lg overflow-hidden border border-grey-light-200">
                <div class="flex-1 flex items-center text-left bg-grey-light-50 px-2 pl-4 w-1/2 border-r">
                  <p class="text-left text-grey-light-600">Cancellation Fee</p>
                </div>
                <div class="font-bold p-2 w-1/2">
                  <p class="grey-light-600 text-right px-2">
                    {{ convertToCurrency(term.fee) }}
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div class="flex justify-between items-center my-4 pl-2">
            <LabelComponent>Auto cancellation</LabelComponent>
            <SwitchComponent v-model="autoCancellation" disabled />
          </div>

          <div>
            <div class="h-auto w-full mb-3">
              <template v-if="autoCancellation">
                <p v-if="missed" class="text-sm text-brand-700">
                  <template v-if="missed === 1">
                    This payment plan will be automatically cancelled if {{ missed }} payment is missed.
                  </template>
                  <template v-else-if="missed > 1">
                    This payment plan will be automatically cancelled if {{ missed }} payments are missed.
                  </template>
                </p>
                <p v-else-if="autoPercentage" class="text-brand-700 text-sm">
                  This order will be automatically cancelled if {{ convertToCurrency(autoAmount) }} ({{
                    autoPercentage
                  }}% of the total amount) is not paid by
                  {{ displayDate(calculateLastPaymentDate(order.createdAt, order.cancellationTerms)) }}.
                </p>
              </template>
              <template v-else>
                <p class="text-sm text-brand-700 text-left">
                  Cancellation of this payment plan and related fees will be subject to
                  {{ order.merchantName ? order.merchantName : widgetStore.paymentPlanPreview.merchant }} approval.
                </p>
              </template>
            </div>
          </div>
        </div>
      </template>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { Button } from '@/components/ui/button';
import { LabelComponent } from '@/components/ui/label';
import SwitchComponent from '@/components/ui/switch/SwitchComponent.vue';
import { useWidgetStore } from '@/stores/widget';
import { displayCurrency, displayDate } from '@/utils/format.utils';
import { calculateLastPaymentDate } from '@/utils/order.utils';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import { ChevronDown } from 'lucide-vue-next';
import { computed, ref } from 'vue';
import type { ModelCancellationPolicyDefinition } from '@layup/api-sdk';

dayjs.extend(localizedFormat);

const showCancellationPolicy = ref(true);

const props = defineProps<{
  deposit: number;
  total: number;
  balance: number;
}>();

const widgetStore = useWidgetStore();
const order = widgetStore.order;

const cancellationTerms = computed(() => {
  let terms: any[] = [];
  if (!order.cancellationTerms) return [{ fee: 0, start: 0, end: 0 }];

  for (const term of order.cancellationTerms.definition!) {
    if (!term.policy?.definition) continue;
    const policy = {
      name: term.policy?.name,
      definition: term.policy?.definition,
      fee: cancellationFee(term.policy.definition),
      start: term.start
        ? dayjs(order.endDateMax)
            .subtract(term.start * -1 - 1, 'days')
            .format('D MMMM YYYY')
        : null,
    };
    terms.push(policy);
  }

  return terms;
});

const convertToCurrency = (amount: number) => {
  if (!amount) return 0;
  return displayCurrency(amount);
};

const cancellationFee = (policy: ModelCancellationPolicyDefinition) => {
  const flatFee = policy.flatFee || 0;
  const percentage = policy.percentage || 0;
  const depositAmount = (policy.deposit && props.deposit) || 0;
  let baseAmount = props.total || 0;
  if (policy?.percentageShouldUseTotalPaid) {
    baseAmount -= props.balance || 0;
  }
  const percentageAmount = (baseAmount || 0) * (percentage / 100);
  return percentageAmount + flatFee + depositAmount;
};

const autoCancellation = computed(() => {
  if (order.cancellationTerms) return order.cancellationTerms.active;
  return false;
});

const missed = computed(() => {
  const lastIndex = order.cancellationTerms?.definition?.length! - 1;
  return order.cancellationTerms?.definition![lastIndex].type?.definition?.missed;
});

const autoPercentage = computed(() => {
  const lastIndex = order.cancellationTerms?.definition?.length! - 1;
  return order.cancellationTerms?.definition![lastIndex].type?.definition?.percentage;
});

const autoAmount = computed(() => {
  return props.total * (autoPercentage.value! / 100);
});
</script>
